import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WAITLIST_DEPOSIT } from '../../marketing/content/waitlist';
import { redirectTo, rememberWaitlistRef, startWaitlistCheckout } from '../../services/waitlistService';

interface ReservePlaceButtonProps {
  /** The opaque waitlist ref returned by joinWaitlist. */
  entryRef: string;
  label?: string;
  className?: string;
}

export const WAITLIST_THANK_YOU_PATH = '/waitlist/thank-you';
export const WAITLIST_CANCEL_PATH = '/waitlist?cancelled=1';

/**
 * The deposit step. Asks the server for a Stripe Checkout Session for this
 * waitlist entry and sends the visitor to Stripe's hosted page; Stripe
 * returns them to /waitlist/thank-you with a session id that the thank-you
 * page confirms server-side. The ref is remembered in sessionStorage so a
 * visitor who backs out of Stripe can resume without re-entering details.
 *
 * On a non-production project with no Stripe keys the server marks the entry
 * paid in "dummy" mode and says so, and we go straight to the thank-you page
 * flagged as a test — the same convention as the enrolment checkout.
 */
export default function ReservePlaceButton({ entryRef, label = WAITLIST_DEPOSIT.label, className = '' }: ReservePlaceButtonProps) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reserve = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    rememberWaitlistRef(entryRef);
    const base = window.location.origin;
    const result = await startWaitlistCheckout({
      ref: entryRef,
      successUrl: `${base}${WAITLIST_THANK_YOU_PATH}`,
      cancelUrl: `${base}${WAITLIST_CANCEL_PATH}`,
    });
    if (!result.success) {
      setError(result.error ?? 'We could not start the payment just now. Please try again.');
      setBusy(false);
      return;
    }
    if (result.alreadyPaid || result.dummy) {
      navigate(`${WAITLIST_THANK_YOU_PATH}?ref=${encodeURIComponent(entryRef)}${result.dummy ? '&dummy=1' : ''}`);
      return;
    }
    if (result.url) {
      redirectTo(result.url);
      return;
    }
    setError('We could not start the payment just now. Please try again.');
    setBusy(false);
  };

  return (
    <div className={`wl-pay__action ${className}`.trim()}>
      <button type="button" className="mk-btn mk-btn--fill" onClick={reserve} disabled={busy}>
        {busy ? WAITLIST_DEPOSIT.busy : label}
      </button>
      {error && (
        <p className="wl-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
